import {
  Box,
  Divider,
  FlexBox,
  Grid,
  Text,
  TextInline,
} from '@trmediaab/zebra';
import { useWindowSize } from '@trmediaab/zebra-hooks';
import { objectKeys } from '@trmediaab/zebra-utils';
import { Fragment, useCallback, useState } from 'react';

import { parseBridle } from '~/utils/equipment';

import { Bridle } from './Bridle';
import {
  renderCarriage,
  renderDriver,
  renderGamePercentage,
  renderHeaderTitle,
  renderHorseName,
  renderHorseNumber,
  renderOdds,
  renderShoes,
  renderSpacer,
  renderTrackInfo,
  renderTrainer,
  renderTrend,
  TextCell,
} from './columns';
import Expanded from './Expanded';
import { getVariant, setShouldExpandStart, shouldExpandStart } from './utils';

const allVariants = ['desktop', 'tablet', 'tabletCompact'];

const getColumns = (variant, betType) =>
  [
    {
      label: 'NR',
      columnWidth: 'min-content',
      render: renderHorseNumber,
      includeIn: allVariants,
    },
    {
      label: 'HÄST',
      columnWidth: '220px',
      render: renderHorseName,
      includeIn: ['desktop', 'tablet'],
    },
    {
      label: 'HÄST / KUSK (TRÄNARE)',
      columnWidth: '220px',
      render: renderHorseName,
      includeIn: ['tabletCompact'],
    },
    {
      label: 'KUSK',
      columnWidth: '144px',
      render: renderDriver,
      includeIn: ['desktop'],
    },
    {
      label: 'KUSK (TRÄNARE)',
      columnWidth: '200px',
      render: renderDriver,
      includeIn: ['tablet'],
    },

    {
      label: 'SPACER',
      columnWidth: '4px',
      render: renderSpacer,
      includeIn: ['desktop'],
    },
    {
      label: 'TRÄNARE',
      columnWidth: '144px',
      render: renderTrainer,
      includeIn: ['desktop'],
    },

    {
      label: 'SPACER',
      columnWidth: '0.5fr',
      render: renderSpacer,
      includeIn: allVariants,
    },
    {
      label: 'BALANS',
      columnWidth: '30px',
      textAlign: 'right',
      render: renderShoes,
      includeIn: allVariants,
    },
    {
      label: 'VAGN',
      textAlign: 'right',
      columnWidth: '42px',
      render: renderCarriage,
      includeIn: allVariants,
    },

    {
      label: 'SPACER',
      columnWidth: '1fr',
      render: renderSpacer,
      includeIn: allVariants,
    },
    ...(betType != null
      ? [
          {
            label: `${betType}-%`,
            textAlign: 'right',
            columnWidth: '50px',
            render: renderGamePercentage,
            includeIn: allVariants,
          },
        ]
      : []),

    ...(betType != null
      ? [
          {
            label: 'TREND',
            textAlign: 'right',
            columnWidth: '50px',
            render: renderTrend,
            includeIn: allVariants,
          },
        ]
      : []),

    {
      label: 'ODDS',
      textAlign: 'right',
      columnWidth: '50px',
      render: renderOdds,
      includeIn: allVariants,
    },

    {
      label: 'SPACER',
      textAlign: 'right',
      columnWidth: '1fr',
      render: renderSpacer,
      includeIn: allVariants,
    },
    {
      label: 'DIST:SPÅR',
      columnWidth: '72px',
      render: renderTrackInfo,
      includeIn: allVariants,
    },
  ].filter(column => column.includeIn.includes(variant));

const DistanceRow = ({ distance }) => (
  <FlexBox
    alignItems="center"
    px="3"
    bg="brands.travrondenSpel"
    gridColumn="1/-1"
    height="36px"
  >
    <Text fontSize="1" fontWeight="bold" color="white">
      Tillägg: {distance} meter
    </Text>
  </FlexBox>
);

const HeaderRow = ({ columns }) => (
  <Box
    display="contents"
    sx={{
      '> *': { bg: 'brands.travrondenSpel', height: '60px' },

      '> :first-child': {
        pl: 3,
      },
    }}
  >
    {columns.map(({ label, textAlign }, idx) => {
      if (label === 'SPACER') {
        return <Fragment key={idx}>{renderSpacer()}</Fragment>;
      }

      return (
        <Fragment key={idx}>
          {renderHeaderTitle({
            label,
            textAlign,
          })}
        </Fragment>
      );
    })}
  </Box>
);

const StartRow = ({
  start,
  betType,
  variant,
  columns,
  gridTemplateColumns,
  isFirst,
  hasPremium,
}) => {
  const hasInterview = start.interviews && start.interviews.length > 0;
  const showExtraRow = variant === 'tabletCompact' || hasInterview;
  const [expanded, setExpanded] = useState(isFirst && shouldExpandStart());
  const bridle = parseBridle(start);

  const handleClick = useCallback(() => {
    setExpanded(prev => !prev);
    setShouldExpandStart();
  }, []);

  return (
    <>
      <Box
        display="contents"
        onClick={handleClick}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',

          '> *': {
            // Since there are two rows fill the space for each item, if there's no extra row
            gridRow: showExtraRow ? undefined : 'span 2',
            transition: 'background-color 0.2s',
            bg: 'white',
            '-webkit-tap-highlight-color': 'transparent',
          },

          '> *:not(:first-child)': {
            pt: showExtraRow ? '20px' : 3,
            pb: showExtraRow ? undefined : 3,
          },

          '> *:first-child': {
            py: 3,
          },

          '&:hover > *': {
            bg: 'greys.0',
          },

          '[data-row2]': {
            pt: 0,
            pb: 3,
          },
        }}
      >
        {columns.map(
          (column, idx) =>
            column.render && (
              <Fragment key={idx}>
                {column.render({ start, variant, betType })}
              </Fragment>
            ),
        )}

        {showExtraRow && (
          <Box gridColumn="2/-1" data-row2 pr="3">
            {variant === 'tabletCompact' &&
              renderDriver({
                start,
                variant,
              })}

            {hasInterview && (
              <Grid
                gridTemplateColumns={gridTemplateColumns.replace(
                  'min-content',
                  '',
                )}
              >
                <Box
                  gridColumnStart="1"
                  gridColumnEnd={variant === 'desktop' ? '5' : '3'}
                >
                  <TextCell>
                    <TextInline
                      color="brands.travrondenSpel"
                      fontWeight="semiBold"
                    >
                      Intervju -{' '}
                    </TextInline>
                    <TextInline
                      color="text.tertiary"
                      fontWeight="semiBold"
                      fontStyle="italic"
                    >
                      {start.interviews[0].teaser}
                    </TextInline>
                  </TextCell>
                </Box>

                {bridle.value != null && hasPremium && (
                  <Box
                    gridColumnStart={
                      variant === 'desktop'
                        ? '6'
                        : variant === 'tablet'
                          ? '4'
                          : '3'
                    }
                    gridColumnEnd="-1"
                    ml="14px"
                  >
                    <TextCell color="text.tertiary">
                      <TextInline
                        color="brands.travrondenSpel"
                        fontWeight="semiBold"
                      >
                        Huvudlag -{' '}
                      </TextInline>

                      <Bridle
                        data={bridle}
                        fontSize="1"
                        fontStyle="normal"
                        display="inline"
                        tooltipEnabled
                      />
                    </TextCell>
                  </Box>
                )}
              </Grid>
            )}
          </Box>
        )}
      </Box>

      {expanded && (
        <Box gridColumn="1/-1">
          <Expanded start={start} betType={betType} />
        </Box>
      )}
    </>
  );
};

const StartListLarge = ({ startsByDistance, betType, hasPremium }) => {
  const { width: windowWidth } = useWindowSize();
  const variant = getVariant(windowWidth);
  const columns = getColumns(variant, betType);
  const gridTemplateColumns = columns.map(col => col.columnWidth).join(' ');

  return (
    <Grid gridTemplateColumns={gridTemplateColumns} gridTemplateRows="auto">
      <HeaderRow columns={columns} />

      {objectKeys(startsByDistance).map((distance, distIdx) => (
        <Fragment key={distance}>
          {Number(distance) > 0 && <DistanceRow distance={distance} />}

          {startsByDistance[distance].map((start, idx, array) => (
            <Fragment key={start.id}>
              <StartRow
                start={start}
                betType={betType}
                columns={columns}
                variant={variant}
                gridTemplateColumns={gridTemplateColumns}
                isFirst={distIdx === 0 && idx === 0}
                hasPremium={hasPremium}
              />
              {idx !== array.length - 1 && (
                <Divider gridColumn="1/-1" variant="startlist" />
              )}
            </Fragment>
          ))}
        </Fragment>
      ))}
    </Grid>
  );
};

export default StartListLarge;
